enum EnumOperator {
  None = 0,
  Add = 1,
  Subtract = 2,
  Multiply = 3,
  Divide = 4,
}

enum HttpStatusCode {
  Continue = 100,
  SwitchingProtocols = 101,
  Processing = 102,
  EarlyHints = 103,
  OK = 200,
  Created = 201,
  Accepted = 202,
  NonAuthoritativeInformation = 203,
  NoContent = 204,
  ResetContent = 205,
  PartialContent = 206,
  MultiStatus = 207,
  AlreadyReported = 208,
  IMUsed = 226,
  Ambiguous = 300,
  MultipleChoices = 300,
  Moved = 301,
  MovedPermanently = 301,
  Found = 302,
  Redirect = 302,
  RedirectMethod = 303,
  SeeOther = 303,
  NotModified = 304,
  UseProxy = 305,
  Unused = 306,
  RedirectKeepVerb = 307,
  TemporaryRedirect = 307,
  PermanentRedirect = 308,
  BadRequest = 400,
  Unauthorized = 401,
  PaymentRequired = 402,
  Forbidden = 403,
  NotFound = 404,
  MethodNotAllowed = 405,
  NotAcceptable = 406,
  ProxyAuthenticationRequired = 407,
  RequestTimeout = 408,
  Conflict = 409,
  Gone = 410,
  LengthRequired = 411,
  PreconditionFailed = 412,
  RequestEntityTooLarge = 413,
  RequestUriTooLong = 414,
  UnsupportedMediaType = 415,
  RequestedRangeNotSatisfiable = 416,
  ExpectationFailed = 417,
  MisdirectedRequest = 421,
  UnprocessableEntity = 422,
  Locked = 423,
  FailedDependency = 424,
  UpgradeRequired = 426,
  PreconditionRequired = 428,
  TooManyRequests = 429,
  RequestHeaderFieldsTooLarge = 431,
  UnavailableForLegalReasons = 451,
  InternalServerError = 500,
  NotImplemented = 501,
  BadGateway = 502,
  ServiceUnavailable = 503,
  GatewayTimeout = 504,
  HttpVersionNotSupported = 505,
  VariantAlsoNegotiates = 506,
  InsufficientStorage = 507,
  LoopDetected = 508,
  NotExtended = 510,
  NetworkAuthenticationRequired = 511,
}

function GetNumber(number: number): number {
  number = isNaN(number) ? 0 : number;
  return number;
}

function StringToNumber(string: string): number {
  string = string.replace(/,/g, "");
  string = string.replace(/\(/g, "");
  string = string.replace(/\)/g, "");
  string = string.trim();
  const number = parseFloat(string);
  return GetNumber(number);
}

function ToRemoveExponential(number: number): string {
  let numberRemoveExponential = number + "";
  if (Math.abs(number) < 1.0) {
    const e = parseInt(number.toString().split("e-")[1]);
    if (e) {
      number *= Math.pow(10, e - 1);
      numberRemoveExponential = "0." + new Array(e).join("0") + number.toString().substring(2);
    }
  } else {
    let e = parseInt(number.toString().split("+")[1]);
    if (e > 20) {
      e -= 20;
      number /= Math.pow(10, e);
      numberRemoveExponential = number + "";
      numberRemoveExponential += new Array(e + 1).join("0");
    }
  }

  return numberRemoveExponential;
}

function ToAccountingRoundUp(number: number, _decimalNumber: number): string {
  _decimalNumber = isNaN(_decimalNumber) ? 2 : _decimalNumber;
  const numberRemoveExponential = number.ToRemoveExponential();
  const numberString = numberRemoveExponential.toString().split(".")[1];
  if (numberString != null && numberString.length > _decimalNumber) {
    const lastNumberDecimalToRoundUp = parseInt(numberString.substring(_decimalNumber, _decimalNumber + 1));
    let numberCalculated = parseFloat(
      numberRemoveExponential.toString().split(".")[0] + "." + numberString.substring(0, _decimalNumber + 1)
    );
    if (lastNumberDecimalToRoundUp == 5) {
      numberCalculated += 1 / Math.pow(10, _decimalNumber + 1);
    }
    return numberCalculated.toFixed(_decimalNumber);
  } else {
    return parseFloat(numberRemoveExponential).toFixed(_decimalNumber);
  }
}

function ToNumberStringFormat(
  amount: number,
  _decimalNumber: number,
  _deciamlSymbol: string,
  _separateSymbol: string
): string {
  let total: number = amount;
  const decimalNumber = isNaN((_decimalNumber = Math.abs(_decimalNumber))) ? 2 : _decimalNumber;
  const decimalSymbol = _deciamlSymbol == undefined ? "." : _deciamlSymbol;
  const separateSymbol = _separateSymbol == undefined ? "," : _separateSymbol;
  let isNegative = total < 0 ? "-" : "";
  total = ToAccountingRoundUp(Math.abs(+total || 0), decimalNumber).ToNumber();
  const roundingAmount = parseInt(total.toString()) + "";
  let separateCount = roundingAmount.length;
  separateCount = separateCount > 3 ? separateCount % 3 : 0;
  const decimalAmount = ToAccountingRoundUp(total, decimalNumber).split(".")[1];
  if (_decimalNumber !== 0) {
    return (
      isNegative +
      (separateCount ? roundingAmount.substr(0, separateCount) + separateSymbol : "") +
      roundingAmount.substr(separateCount).replace(/(\d{3})(?=\d)/g, "$1" + separateSymbol) +
      decimalSymbol +
      decimalAmount
    );
  } else {
    isNegative = parseInt(amount.toString()) < 0 ? "-" : "";
    return (
      isNegative +
      (separateCount ? roundingAmount.substr(0, separateCount) + separateSymbol : "") +
      roundingAmount.substr(separateCount).replace(/(\d{3})(?=\d)/g, "$1" + separateSymbol)
    );
  }
}

function PeakOperator(variable1: number, variable2: number, operator: EnumOperator): number {
  let output = 0;
  variable1 = variable1.ToNumber();
  variable2 = variable2.ToNumber();
  const decimalNumberVariable1 = variable1.ToNumber().GetDecimalNumber();
  const decimalNumberVariable2 = variable2.ToNumber().GetDecimalNumber();
  let decimalNumber = 1;
  if (decimalNumberVariable1 !== 0 || decimalNumberVariable2 !== 0) {
    decimalNumber = decimalNumberVariable1 > decimalNumberVariable2 ? decimalNumberVariable1 : decimalNumberVariable2;
  }
  const divisor = Math.pow(10, decimalNumber);
  switch (operator) {
    case EnumOperator.Add:
      output = (variable1 * divisor + variable2 * divisor) / divisor;
      break;
    case EnumOperator.Subtract:
      output = (variable1 * divisor - variable2 * divisor) / divisor;
      break;
    case EnumOperator.Multiply:
      output = (variable1 * divisor * (variable2 * divisor)) / (divisor * divisor);
      break;
    case EnumOperator.Divide:
      output = (variable1 * divisor) / (variable2 * divisor);
      break;
  }
  return output;
}
function PeakOperatorAdd(variable1: number, variable2: number): number {
  return PeakOperator(variable1, variable2, EnumOperator.Add);
}
function PeakOperatorSubtract(variable1: number, variable2: number): number {
  return PeakOperator(variable1, variable2, EnumOperator.Subtract);
}
function PeakOperatorMultiply(variable1: number, variable2: number): number {
  return PeakOperator(variable1, variable2, EnumOperator.Multiply);
}
function PeakOperatorDivide(variable1: number, variable2: number): number {
  return PeakOperator(variable1, variable2, EnumOperator.Divide);
}

function GetDecimalNumber(number: number): number {
  let decimalNumber = 0;
  const numberRemoveExponential = number.ToRemoveExponential();
  const numberString = numberRemoveExponential.toString().split(".")[1];
  if (numberString) {
    decimalNumber = numberString.length;
  }
  return decimalNumber;
}

function ToDateJsonString(date: string | Date) {
  const newDate = new Date(date);
  return (
    newDate.getFullYear() +
    "-" +
    ("0" + (newDate.getMonth() + 1)).slice(-2) +
    "-" +
    ("0" + newDate.getDate()).slice(-2) +
    "T00:00:00"
  );
}

function GetDateFormat(date: string | Date) {
  const start = new Date(date);
  let day = start.getDate().toString();
  let month = (parseInt(start.getMonth().toString(), 10) + 1).toString();
  const year = start.getFullYear();

  day = day.length == 2 ? day : "0" + day;
  month = month.length == 2 ? month : "0" + month;

  const dateFormat = day + "/" + month + "/" + year;

  if (date == null || isNaN(day.ToNumber()) || isNaN(month.ToNumber()) || isNaN(year)) {
    return "";
  } else {
    return dateFormat;
  }
}

function GetTimeFormat(date: string | Date) {
  const start = new Date(date);
  let hours = start.getHours().toString();
  let minutes = start.getMinutes().toString();

  hours = hours.length == 2 ? hours : "0" + hours;
  minutes = minutes.length == 2 ? minutes : "0" + minutes;

  const timeFormat = hours + ":" + minutes;

  if (date == null || isNaN(hours.ToNumber()) || isNaN(minutes.ToNumber())) {
    return "";
  } else {
    return timeFormat;
  }
}

function GetDateTimeFormat(_date: string | Date, _type: string) {
  let newDate = new Date(_date);
  newDate = new Date(newDate.getTime() - newDate.getTimezoneOffset() * 60000);

  const fullYear = newDate.getFullYear().toString();
  const month = (newDate.getMonth() + 1).toString();
  const date = newDate.getDate().toString();
  const hours = newDate.getHours().toString();
  const minutes = newDate.getMinutes().toString();
  const seconds = newDate.getSeconds().toString();
  if (_type === "date") {
    return `${date.length == 2 ? date : "0" + date}/${month.length == 2 ? month : "0" + month}/${fullYear}`;
  } else {
    return `${hours.length == 2 ? hours : "0" + hours}:${minutes.length == 2 ? minutes : "0" + minutes}`;
  }
}

function GetMonthYearFormat(date = new Date()) {
  const start: any = new Date(date);
  let month: any = (parseInt(start.getMonth(), 10) + 1).toString();
  const year: any = start.getFullYear();

  month = month.length == 2 ? month : "0" + month;

  const dateFormat = year + "/" + month;
  if (date == null || isNaN(month) || isNaN(year)) {
    return "-";
  } else {
    return dateFormat;
  }
}
function GetMonthYearFormatString(date: string) {
  const start = new Date(date);
  let month = (parseInt(start.getMonth().toString(), 10) + 1).toString();
  const year = start.getFullYear();

  month = month.length == 2 ? month : "0" + month;

  const dateFormat = year + "/" + month;

  if (date == null || isNaN(month.ToNumber()) || isNaN(year)) {
    return "";
  } else {
    return dateFormat;
  }
}
function RegExpThai(str: string) {
  const RegExr = /^[ก-๙0-9$@$!%*?&#-^-_. ;:+()]+$/g;
  const text = str.toLowerCase().match(RegExr);
  return text == null ? false : true;
}

function RegExpEnglish(str: string) {
  const RegExr = /^[a-zA-Z0-9$@$!%*?&#-^-_. ;:+()]+$/g;
  const text = str.toLowerCase().match(RegExr);
  return text == null ? false : true;
}

function FormatPhoneNumber(phoneNumber: string) {
  let phoneNumberFormat = "";
  if (phoneNumber.length > 6) {
    if (phoneNumber.length > 9) {
      const group1 = phoneNumber.substr(0, 3);
      const group2 = phoneNumber.substr(3, 3);
      const group3 = phoneNumber.substr(6, phoneNumber.length);
      phoneNumberFormat = `${group1}-${group2}-${group3}`;
    } else {
      const group1 = phoneNumber.substr(0, 2);
      const group2 = phoneNumber.substr(2, 3);
      const group3 = phoneNumber.substr(6, phoneNumber.length);
      phoneNumberFormat = `${group1}-${group2}-${group3}`;
    }
  } else {
    phoneNumberFormat = phoneNumber;
  }
  return phoneNumberFormat;
}

function ThaiBaht(amount: number) {
  let bahtTxt = "";
  let n = "";
  let bahtTH = "";
  const intValSplit = [];
  if (amount < 0) {
    bahtTH = "ลบ";
    amount = amount * -1;
  }

  bahtTxt = amount.FormatMoney().replace(/,/g, "");
  const num = ["ศูนย์", "หนึ่ง", "สอง", "สาม", "สี่", "ห้า", "หก", "เจ็ด", "แปด", "เก้า", "สิบ"];
  const rank = ["", "สิบ", "ร้อย", "พัน", "หมื่น", "แสน", "ล้าน"];
  const temp = bahtTxt.split(".");
  let intVal: any = temp[0];
  const decVal = temp[1];
  if (bahtTxt.ToNumber() == 0) {
    bahtTH = "ศูนย์บาทถ้วน";
  } else {
    while (intVal) {
      let splitVal = "";
      if (intVal.length > 6) {
        splitVal = intVal.substr(intVal.length - 6, 6);
        intVal = intVal.substr(0, intVal.length - 6);
      } else {
        splitVal = intVal;
        intVal = null;
      }
      intValSplit.push(splitVal);
    }

    intValSplit.reverse();
    let intValSplitCount = intValSplit.length;

    intValSplit.forEach((value) => {
      let intcount = 0;
      for (let i = 0; i < value.length; i++) {
        n = value.substr(i, 1);
        if (n != "0") {
          if (intcount >= 1 && i == value.length - 1 && n == "1") {
            bahtTH += "เอ็ด";
          } else if (i == value.length - 2 && n == "2") {
            bahtTH += "ยี่";
          } else if (i == value.length - 2 && n == "1") {
            bahtTH += "";
          } else {
            bahtTH += num[n.ToNumber()];
          }

          bahtTH += rank[value.length - i - 1];
          intcount++;
        }
      }
      if (intValSplitCount != 1) {
        bahtTH += rank[6];
      }
      intValSplitCount--;
    });

    if (bahtTH.trim().length > 0) {
      bahtTH += "บาท";
    }
    if (decVal == "00") {
      if (bahtTH.trim().length > 0) {
        bahtTH += "ถ้วน";
      }
    } else {
      let deccount = 0;

      for (let i = 0; i < decVal.length; i++) {
        n = decVal.substr(i, 1);
        if (n != "0") {
          if (deccount >= 1 && i == decVal.length - 1 && n == "1") {
            bahtTH += "เอ็ด";
          } else if (i == decVal.length - 2 && n == "2") {
            bahtTH += "ยี่";
          } else if (i == decVal.length - 2 && n == "1") {
            bahtTH += "";
          } else {
            bahtTH += num[n.ToNumber()];
          }
          bahtTH += rank[decVal.length - i - 1];
          deccount++;
        }
      }
      bahtTH += "สตางค์";
    }
  }

  return bahtTH;
}

function ValidateEmail(email: string) {
  // eslint-disable-next-line no-empty-character-class
  const re = /^((?!^[.])(?!.*[.]$)(?!.*[.]{2})[0-9A-Za-z]+((?!^[.])(?!.*[.]$)(?!.*[.]{2})[\w.'-]{1,64}[-!#-'*+/-9=?A-Z^-~]+)*|"([]!#-[^-~ \t]|(\\[\t -~]))+")@([0-9A-Za-z]([0-9A-Za-z-]{0,61}[0-9A-Za-z])?(\.[0-9A-Za-z]([0-9A-Za-z-]{0,61}[0-9A-Za-z])?)+|\[((25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])(\.(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])){3}|IPv6:((((0|[1-9A-Fa-f][0-9A-Fa-f]{0,3}):){6}|::((0|[1-9A-Fa-f][0-9A-Fa-f]{0,3}):){5}|[0-9A-Fa-f]{0,4}::((0|[1-9A-Fa-f][0-9A-Fa-f]{0,3}):){4}|(((0|[1-9A-Fa-f][0-9A-Fa-f]{0,3}):)?(0|[1-9A-Fa-f][0-9A-Fa-f]{0,3}))?::((0|[1-9A-Fa-f][0-9A-Fa-f]{0,3}):){3}|(((0|[1-9A-Fa-f][0-9A-Fa-f]{0,3}):){0,2}(0|[1-9A-Fa-f][0-9A-Fa-f]{0,3}))?::((0|[1-9A-Fa-f][0-9A-Fa-f]{0,3}):){2}|(((0|[1-9A-Fa-f][0-9A-Fa-f]{0,3]):){0,3}(0|[1-9A-Fa-f][0-9A-Fa-f]{0,3}))?::(0|[1-9A-Fa-f][0-9A-Fa-f]{0,3}):|(((0|[1-9A-Fa-f][0-9A-Fa-f]{0,3]):){0,4}(0|[1-9A-Fa-f][0-9A-Fa-f]{0,3}))?::)((0|[1-9A-Fa-f][0-9A-Fa-f]{0,3}):(0|[1-9A-Fa-f][0-9A-Fa-f]{0,3})|(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])(\.(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])){3})|(((0|[1-9A-Fa-f][0-9A-Fa-f]{0,3}):){0,5}(0|[1-9A-Fa-f][0-9A-Fa-f]{0,3}))?::(0|[1-9A-Fa-f][0-9A-Fa-f]{0,3})|(((0|[1-9A-Fa-f][0-9A-Fa-f]{0,3]):){0,6}(0|[1-9A-Fa-f][0-9A-Fa-f]{0,3}))?::)|(?!IPv6:)[0-9A-Za-z-]*[0-9A-Za-z]:[!-Z^-~]+)])$/;
  return re.test(String(email).toLowerCase());
}

function IsTaxNumber(taxIdNumber: string) {
  if (taxIdNumber && taxIdNumber.length == 13) {
    let sum = 0;
    for (let i = 0; i < taxIdNumber.length - 1; i++) {
      const n = parseInt(taxIdNumber[i]);
      sum = sum + (13 - i) * n;
    }
    const resultSum = (11 - (sum % 11)) % 10;
    const checkSum = parseInt(taxIdNumber[12]);
    return resultSum == checkSum;
  }
  return false;
}

function UrlCombine(uri: string, ...segments: string[]): string | null {
  if (!uri || !uri.trim()) {
    return null;
  }

  const normalizedBase = uri.endsWith("/") ? uri.slice(0, -1) : uri;
  const normalizedSegments = segments.map((segment) =>
    segment.trim().startsWith("/") ? segment.slice(1).trim() : segment.trim()
  );

  return `${normalizedBase}/${normalizedSegments.join("/")}`;
}

String.prototype.UrlCombine = function(this: string, ...segments: string[]): string | null {
  return UrlCombine(this, ...segments);
};

String.prototype.IsTaxNumber = function(this: string): boolean {
  return IsTaxNumber(this);
};

String.prototype.FormatPhoneNumber = function(this: string): string {
  return FormatPhoneNumber(this);
};

String.prototype.PeakEncryption = function(this: string): string {
  return btoa(this);
};

Number.prototype.PeakEncryption = function(this: number): string {
  const str = this.toString();
  return btoa(str);
};

String.prototype.PeakDecryption = function(this: string): number {
  let value = 0;
  try {
    value = atob(this).ToNumber();
  } catch {
    value = 0;
  }
  return value;
};

String.prototype.ToNumber = function(this: string): number {
  return StringToNumber(this);
};

Number.prototype.ToNumber = function(this: number): number {
  return GetNumber(this);
};

String.prototype.ToAccountingRoundUp = function(this: string): string {
  return ToAccountingRoundUp(this.ToNumber(), 2);
};

Number.prototype.ToAccountingRoundUp = function(this: number): string {
  return ToAccountingRoundUp(this, 2);
};

Number.prototype.ToRemoveExponential = function(this: number): string {
  return ToRemoveExponential(this);
};

String.prototype.FormatMoney = function(this: string, decimalNumber?: number): string {
  if (decimalNumber == undefined) {
    decimalNumber = 2;
  }
  return ToNumberStringFormat(this.ToNumber(), decimalNumber, ".", ",");
};

Number.prototype.FormatMoney = function(this: number, decimalNumber?: number): string {
  if (decimalNumber == undefined) {
    decimalNumber = 2;
  }
  return ToNumberStringFormat(this, decimalNumber, ".", ",");
};
String.prototype.PeakFormatMoney = function(this: string, decimalNumber?: number): string {
  if (decimalNumber == undefined) {
    decimalNumber = 2;
  }
  const value = this.ToNumber();
  if (value < 0) {
    return "(" + ToNumberStringFormat(value * -1, decimalNumber, ".", ",") + ")";
  } else {
    return ToNumberStringFormat(value, decimalNumber, ".", ",");
  }
};

Number.prototype.PeakFormatMoney = function(this: number, decimalNumber?: number): string {
  if (decimalNumber == undefined) {
    decimalNumber = 2;
  }
  if (this < 0) {
    return "(" + ToNumberStringFormat(this * -1, decimalNumber, ".", ",") + ")";
  } else {
    return ToNumberStringFormat(this, decimalNumber, ".", ",");
  }
};

Number.prototype.Add = function(this: number, variable: number): number {
  return PeakOperatorAdd(this, variable);
};
Number.prototype.Subtract = function(this: number, variable: number): number {
  return PeakOperatorSubtract(this, variable);
};
Number.prototype.Multiply = function(this: number, variable: number): number {
  return PeakOperatorMultiply(this, variable);
};
Number.prototype.Divide = function(this: number, variable: number): number {
  return PeakOperatorDivide(this, variable);
};

Number.prototype.GetDecimalNumber = function(this: number): number {
  return GetDecimalNumber(this);
};

Number.prototype.ToThaiBaht = function(this: number): string {
  return ThaiBaht(this);
};

String.prototype.ToDateJsonString = function(this: string): string {
  return ToDateJsonString(this);
};
Date.prototype.ToDateJsonString = function(this: Date): string {
  return ToDateJsonString(this);
};
Date.prototype.GetDateFormat = function(this: Date): string {
  return GetDateFormat(this);
};
Date.prototype.GetTimeFormat = function(this: Date): string {
  return GetTimeFormat(this);
};
Date.prototype.GetDateTimeFormat = function(this: Date, type: string): string {
  return GetDateTimeFormat(this, type);
};
Date.prototype.GetMonthYearFormat = function(this: Date): string {
  return GetMonthYearFormat(this);
};
String.prototype.GetDateFormat = function(this: string): string {
  return GetDateFormat(this);
};
String.prototype.GetDateTimeFormat = function(this: string, type: string): string {
  return GetDateTimeFormat(this, type);
};
String.prototype.GetTimeFormat = function(this: string): string {
  return GetTimeFormat(this);
};
String.prototype.GetMonthYearFormat = function(this: string): string {
  return GetMonthYearFormatString(this);
};
Number.prototype.IsSuccess = function(this: number): boolean {
  return this === HttpStatusCode.OK;
};
String.prototype.IsSuccess = function(this: string): boolean {
  return this.ToNumber() === HttpStatusCode.OK;
};
Number.prototype.IsSeeOther = function(this: number): boolean {
  return this === HttpStatusCode.SeeOther;
};
String.prototype.IsSeeOther = function(this: string): boolean {
  return this.ToNumber() === HttpStatusCode.SeeOther;
};
Number.prototype.Unauthorized = function(this: number): boolean {
  return this === HttpStatusCode.Unauthorized;
};
String.prototype.Unauthorized = function(this: string): boolean {
  return this.ToNumber() === HttpStatusCode.Unauthorized;
};
String.prototype.RegExpThai = function(this: string): boolean {
  return RegExpThai(this);
};
String.prototype.RegExpEnglish = function(this: string) {
  return RegExpEnglish(this);
};
Date.prototype.GetUTCTime = function() {
  return new Date(
    this.getUTCFullYear(),
    this.getUTCMonth(),
    this.getUTCDate(),
    this.getUTCHours(),
    this.getUTCMinutes(),
    this.getUTCSeconds()
  ).getTime();
};

String.prototype.ValidateEmail = function() {
  return ValidateEmail(this);
};
interface String {
  PeakEncryption(this: string): string;
  PeakDecryption(this: string): number;
  ToNumber(this: string): number;
  ToAccountingRoundUp(this: string): string;
  FormatMoney(this: string, decimalNumber?: number): string;
  PeakFormatMoney(this: string, decimalNumber?: number): string;
  ToDateJsonString(this: string): string;
  GetDateFormat(this: string): string;
  GetTimeFormat(this: string): string;
  GetDateTimeFormat(this: string, type: string): string;
  GetMonthYearFormat(this: string): string;
  IsSuccess(this: string): boolean;
  IsSeeOther(this: string): boolean;
  Unauthorized(this: string): boolean;
  RegExpThai(this: string): boolean;
  RegExpEnglish(this: string): boolean;
  FormatPhoneNumber(this: string): string;
  IsTaxNumber(this: string): boolean;
  ValidateEmail(this: string): boolean;
  UrlCombine(this: string, ...segments: string[]): string | null;
}
interface Number {
  PeakEncryption(this: number): string;
  ToNumber(this: number): number;
  ToAccountingRoundUp(this: number): string;
  ToRemoveExponential(this: number): string;
  FormatMoney(this: number, decimalNumber?: number): string;
  PeakFormatMoney(this: number, decimalNumber?: number): string;
  Add(this: number, variable: number): number;
  Subtract(this: number, variable: number): number;
  Multiply(this: number, variable: number): number;
  Divide(this: number, variable: number): number;
  GetDecimalNumber(this: number): number;
  IsSuccess(this: number): boolean;
  IsSeeOther(this: number): boolean;
  Unauthorized(this: number): boolean;
  ToThaiBaht(this: number): string;
}

interface Date {
  ToDateJsonString(this: Date): string;
  GetDateFormat(this: Date): string;
  GetTimeFormat(this: Date): string;
  GetDateTimeFormat(this: Date, type: string): string;
  GetMonthYearFormat(this: Date): string;
  GetUTCTime(this: Date): number;
}
